import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/auth';
import { AuthShell, AuthCard, AuthInput, AuthButton, AuthError, AuthSuccess } from '../components/AuthShell';
import { motion, AnimatePresence, springs } from '../lib/motion';

export function ForgotPasswordPage() {
  const nav = useNavigate();
  const { forgotPassword, confirmForgotPassword, loading, error, clearError } = useAuthStore();
  const [step, setStep] = useState<'request' | 'confirm'>('request');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [success, setSuccess] = useState<string | null>(null);

  const requestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    const r = await forgotPassword(email.trim().toLowerCase());
    if (r.ok) {
      setSuccess('Check your email for a 6-digit code.');
      setStep('confirm');
    }
  };

  const reset = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    const r = await confirmForgotPassword(email.trim().toLowerCase(), code.trim(), newPassword);
    if (r.ok) {
      nav('/login', { state: { email } });
    }
  };

  return (
    <AuthShell>
      <AuthCard
        title={step === 'request' ? 'Reset your password' : 'Set a new password'}
        subtitle={step === 'request' ? 'We\'ll email you a code.' : 'Enter the code we sent and your new password.'}
      >
        <AnimatePresence mode="wait" initial={false}>
          {step === 'request' ? (
            <motion.form
              key="request"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={springs.soft}
              onSubmit={requestCode}
              className="space-y-4"
            >
              <AuthInput type="email" label="Email" value={email} onChange={setEmail} required autoFocus />
              {error && <AuthError message={error} />}
              <AuthButton type="submit" loading={loading}>Send code</AuthButton>
            </motion.form>
          ) : (
            <motion.form
              key="confirm"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={springs.soft}
              onSubmit={reset}
              className="space-y-4"
            >
              {success && <AuthSuccess message={success} />}
              <AuthInput
                label="6-digit code" value={code} onChange={setCode} required autoFocus
                inputMode="numeric" placeholder="123456"
                inputClassName="text-center font-mono text-lg tracking-[0.4em]"
              />
              <AuthInput type="password" label="New password" value={newPassword} onChange={setNewPassword}
                         autoComplete="new-password" required
                         placeholder="At least 10 chars, with upper/lower/number" />
              {error && <AuthError message={error} />}
              <AuthButton type="submit" loading={loading}>Reset password</AuthButton>
            </motion.form>
          )}
        </AnimatePresence>
        <p className="mt-4 text-center text-sm">
          <Link to="/login" className="inline-flex min-h-[44px] items-center gap-1 text-slate-500 transition-colors hover:text-squishy-navy hover:underline">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
            </svg>
            Back to sign in
          </Link>
        </p>
      </AuthCard>
    </AuthShell>
  );
}
